/* eslint-disable no-param-reassign */
import { type User, type UserFormData, FirmsDTO } from '@types';
import { Select as DaisySelect } from 'react-daisyui';
import { Controller, UseFormReturn } from 'react-hook-form';

import { useEffect, useState, ReactElement } from 'react';
import { Spinner } from '@/components/Spinner';
import useFirms from '@/hooks/useFirms';
import ClientListInput from '@/components/UserList/ClientListInput';

// eslint-disable-next-line import/prefer-default-export
export function EditUserClientList({
  user,
  formData,
  firmClientsListIds,
}: {
  user: User;
  formData: UseFormReturn<UserFormData>;
  firmClientsListIds: number[];
}) {
  const { Option } = DaisySelect;
  const { useGetFirmsWithClientByUserId } = useFirms();
  const { data: firms, isValidating } = useGetFirmsWithClientByUserId(
    user?.id || 0
  );
  const [selectedFirm, setSelectedFirm] = useState<FirmsDTO | undefined>(
    undefined
  );

  /*
    we need to live-update showing the client list based
    on the admin role
  */
  const roleInputValue = formData.watch('role');

  useEffect(() => {
    if (firms && firms.length > 0) {
      setSelectedFirm(firms[0]);
    }
  }, [firms]);

  if (isValidating) {
    return <Spinner />;
  }

  return (
    // eslint-disable-next-line @typescript-eslint/no-misused-promises
    <>
      {roleInputValue === 'admin' || roleInputValue === 'client' ? (
        <div className='flex flex-wrap gap-4 pb-2 mt-4'>
          <div className='capitalize w-[23%] text-sm text-right pr-3'>
            Clients
          </div>
          <div className='font-bold text-sm w-[73%] text-left pl-3'>
            <Controller
              name='clients'
              control={formData.control}
              render={({ field }) => (
                <ClientListInput
                  // eslint-disable-next-line react/jsx-props-no-spreading
                  {...{ ...field, ref: null }}
                  multi={user.role !== 'client'}
                  showWarning={user.role === 'admin'}
                />
              )}
            />
          </div>
        </div>
      ) : null}
      {roleInputValue === 'investor' ? (
        <>
          <div className='flex flex-wrap gap-4 pb-2 mt-4'>
            <div className='capitalize w-[23%] text-sm text-right pr-3'>
              {user.firms && user.firms?.length > 0
                ? 'Independent Clients'
                : 'Clients'}
            </div>
            <div className='font-bold text-sm w-[73%] text-left pl-3'>
              <Controller
                name='clients'
                control={formData.control}
                render={({ field }) => (
                  <ClientListInput
                    // eslint-disable-next-line react/jsx-props-no-spreading
                    {...{ ...field, ref: null }}
                    multi
                    excludedOptions={firmClientsListIds}
                  />
                )}
              />
            </div>
          </div>

          {user.firms && user.firms?.length > 0 && selectedFirm ? (
            <>
              <div className='flex flex-wrap gap-4 pb-2 mt-4 items-center'>
                <div className='capitalize w-[23%] text-sm text-right pr-3 font-bold'>
                  Firm Clients
                </div>
                <div className='font-bold text-sm w-[73%] text-left pl-3'>
                  <DaisySelect
                    value={selectedFirm.id}
                    size='sm'
                    className='select select-bordered select-accent '
                    onChange={(selectedFirmId: number) => {
                      const firm = (firms ?? []).find(
                        (f) => f.id === Number(selectedFirmId)
                      );
                      setSelectedFirm(firm);
                    }}
                  >
                    <Option value={0} disabled>
                      Select a firm
                    </Option>
                    {
                      firms?.map((firm) => (
                        <Option key={`firm_${firm.firm_name}`} value={firm.id}>
                          {firm.firm_name}
                        </Option>
                      )) as unknown as ReactElement
                    }
                  </DaisySelect>
                </div>
              </div>
              <div className='flex flex-wrap gap-4 pb-2 mt-4'>
                <div className='w-[23%] text-sm text-right pr-3'>
                  Clients for {selectedFirm.firm_name}
                </div>
                <div className='font-bold text-sm w-[73%] text-left pl-3'>
                  <ClientListInput
                    value={(selectedFirm.firm_clients || []).map((fc) => {
                      return {
                        value: fc.client?.id as number,
                        label: fc.client?.client_name || '',
                      };
                    })}
                    multi
                    onChange={() => {}}
                    isDisabled
                  />
                </div>
              </div>
            </>
          ) : null}
        </>
      ) : null}
    </>
  );
}
